import drapeiqLogo from "/CS_Software_DinoLogo.png";

import { useState, useCallback, useMemo, useEffect } from "react";
import LandingScreen from "./components/LandingScreen";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import { saveNewQuote, saveRevision, peekNextQuoteNo, getTenantDetails, resetTenantCache } from "./lib/quotes";
import { supabase } from "./lib/supabase";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const DEFAULT_ROOM_TYPES = ["LIVING","DINING","MBR","CBR1","CBR2","CBR3","GBR1","GBR2","GBR3","GYM","STUDY","HOMETHEATER","BAR","PLAY","LIB","BALCONY1","BALCONY2","BALCONY3"];
const DEFAULT_FLOORS = ["GF","FF","SF","TF"];

const CURTAIN_SEGMENT = {
  "Curtain/Sheer": [
    {type:"Eyelet with Lock", stitchRate:400},
    {type:"Eyelet without Lock", stitchRate:350},
    {type:"3 Pleat Lining", stitchRate:350},
    {type:"3 Pleat", stitchRate:300},
    {type:"Rod Pocket", stitchRate:275},
    {type:"Loops", stitchRate:275},
  ],
  "Roman": [{type:"Roman", stitchRate:115}],
  "Readymade": [
    {type:"Roller", stitchRate:0},
    {type:"Zebra", stitchRate:0},
    {type:"Custom Roller", stitchRate:0},
    {type:"Wooden Blinds", stitchRate:0},
    {type:"Custom Wooden", stitchRate:0},
  ]
};

const ALL_MANUFACTURERS = ["Nu Home","KC","D Decor","Nanak & Co","SV Decor","Sol Homes","GM","Mista Decor","Deva","Home Fabrics","Balaji Decor","CPW","Spin Decor"];
const CURTAIN_ROD_OPTIONS = ["Curtain Rod (SS)","Curtain Rod (Antique)","M-Track"];
const ACCESSORY_OPTIONS = ["Finials","End Caps","Single Bracket","Double Bracket","Wall Bracket","Tie Hooks"];
const FITTING_RATES = [250,300,500];
const TERMS = [
  "80% payable in advance",
  "Transport / labour charges (if any) not included",
  "Delivery - within 15 days of confirmation & advance payment"
];
const APP_PASSWORD = "smd2026";

// ─── THEME ────────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg:"#0f0e0d", bg2:"#1a1917", bg3:"#242220", bg4:"#2e2c2a",
    border:"#3a3835",
    pink:"#9b2d7f", pinkLight:"#c05fa0", pinkDim:"#6b1a5a",
    text:"#f5f0eb", textMuted:"#9e9890", textDim:"#6b6560",
    green:"#059669", red:"#dc2626", blue:"#60a5fa",
    inputBg:"#2e2c2a", roomBodyBg:"rgba(15,14,13,0.5)",
    winExtraBg:"rgba(0,0,0,0.1)", panelRowBg:"rgba(0,0,0,0.15)",
    termsBg:"rgba(26,25,23,0.5)", floorTabActiveBg:"#9b2d7f",
    floorTabBg:"rgba(155,45,127,0.08)", roomTabActiveBg:"rgba(155,45,127,0.15)",
    roomTabBg:"transparent",
  },
  light: {
    bg:"#f5f0f4", bg2:"#ffffff", bg3:"#f0eaf0", bg4:"#e8e0e8",
    border:"#ddd0dc",
    pink:"#9b2d7f", pinkLight:"#b03a90", pinkDim:"#6b1a5a",
    text:"#1a1014", textMuted:"#5a4060", textDim:"#8a7088",
    green:"#059669", red:"#dc2626", blue:"#2563eb",
    inputBg:"#f0eaf0", roomBodyBg:"#faf7fa",
    winExtraBg:"rgba(155,45,127,0.03)", panelRowBg:"rgba(155,45,127,0.02)",
    termsBg:"#f0eaf0", floorTabActiveBg:"#9b2d7f",
    floorTabBg:"rgba(155,45,127,0.06)", roomTabActiveBg:"rgba(155,45,127,0.12)",
    roomTabBg:"transparent",
  }
};

// ─── TYPOGRAPHY SYSTEM ────────────────────────────────────────────────────────
// Consistent font sizes: heading1=20, heading2=15, heading3=13, label=11, body=13, small=11, tiny=10
const FONT = {
  heading1: { fontSize:20, fontWeight:700, letterSpacing:"-0.3px" },
  heading2: { fontSize:15, fontWeight:700, letterSpacing:"0" },
  heading3: { fontSize:13, fontWeight:700 },
  sectionLabel: { fontSize:11, fontWeight:700, letterSpacing:"2px", textTransform:"uppercase" },
  fieldLabel: { fontSize:11, fontWeight:600, letterSpacing:"0.5px", textTransform:"uppercase" },
  body: { fontSize:13, fontWeight:400 },
  bodyMed: { fontSize:13, fontWeight:500 },
  small: { fontSize:11, fontWeight:400 },
  smallBold: { fontSize:11, fontWeight:700 },
  tiny: { fontSize:10, fontWeight:400 },
  tinyBold: { fontSize:10, fontWeight:700 },
  mono: { fontFamily:"'Courier New',monospace", fontWeight:700 },
};

const makeStyles = (C) => ({
  page:{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:"'Segoe UI',system-ui,-apple-system,sans-serif", transition:"background 0.25s,color 0.25s", ...FONT.body },
  topbar:{ position:"sticky", top:0, zIndex:50, background:C.bg2, borderBottom:`1px solid ${C.border}`, padding:"12px 28px", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"background 0.25s" },
  subtitle:{ ...FONT.tiny, letterSpacing:"3px", color:C.textDim, marginTop:3, textTransform:"uppercase" },
  grandLabel:{ ...FONT.tinyBold, letterSpacing:"2px", color:C.textMuted, textTransform:"uppercase" },
  grandValue:{ ...FONT.mono, fontSize:24, color:C.pink },
  previewBtn:{ background:C.pink, color:"#fff", ...FONT.smallBold, border:"none", borderRadius:8, padding:"10px 20px", cursor:"pointer" },
  themeBtn:{ background:"transparent", border:`1px solid ${C.border}`, borderRadius:8, padding:"7px 11px", cursor:"pointer", fontSize:16, color:C.text, lineHeight:1 },
  container:{ maxWidth:1300, margin:"0 auto", padding:"24px 20px" },
  card:{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:20, transition:"background 0.25s" },
  sectionHeading:{ ...FONT.sectionLabel, color:C.textMuted, marginBottom:14, display:"block" },
  grid4:{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 },
  grid2:{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:14 },
  fieldLabel:{ ...FONT.fieldLabel, color:C.textMuted, marginBottom:5, display:"block" },
  input:{ width:"100%", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:7, padding:"9px 12px", color:C.text, ...FONT.body, outline:"none", boxSizing:"border-box", transition:"border-color 0.15s" },
  select:{ width:"100%", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:7, padding:"9px 12px", color:C.text, ...FONT.body, outline:"none", cursor:"pointer", boxSizing:"border-box" },
  smallInput:{ background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:5, padding:"5px 8px", color:C.text, ...FONT.small, outline:"none", boxSizing:"border-box" },
  // Floor+Room tab layout
  floorBlock:{ marginBottom:24, border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden" },
  floorHeader:{ background:C.bg3, padding:"12px 18px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${C.border}` },
  floorTitle:{ ...FONT.heading2, color:C.pink },
  roomTabBar:{ background:C.bg3, borderBottom:`1px solid ${C.border}`, display:"flex", overflowX:"auto", gap:2, padding:"0 8px" },
  roomTabActive:{ ...FONT.smallBold, color:"#fff", background:C.pink, border:"none", borderRadius:"6px 6px 0 0", padding:"8px 16px", cursor:"pointer", whiteSpace:"nowrap", marginTop:6 },
  roomTabInactive:{ ...FONT.small, color:C.textMuted, background:C.roomTabBg, border:`1px solid ${C.border}`, borderBottom:"none", borderRadius:"6px 6px 0 0", padding:"8px 16px", cursor:"pointer", whiteSpace:"nowrap", marginTop:6 },
  roomBody:{ background:C.roomBodyBg, padding:20 },
  winWrapper:{ border:`1px solid rgba(155,45,127,0.18)`, borderRadius:10, marginBottom:16, overflow:"hidden" },
  winHeader:{ background:"rgba(155,45,127,0.06)", padding:"9px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 },
  th:{ background:C.bg3, padding:"9px 10px", textAlign:"left", color:C.textMuted, ...FONT.tinyBold, letterSpacing:"1px", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" },
  td:{ padding:"7px 8px", borderBottom:`1px solid rgba(155,45,127,0.07)`, verticalAlign:"middle" },
  tdAmt:{ padding:"7px 8px", borderBottom:`1px solid rgba(155,45,127,0.07)`, textAlign:"right", ...FONT.mono, color:C.pink, fontSize:13 },
  addBtn:{ background:"transparent", border:`1px solid rgba(155,45,127,0.3)`, color:"rgba(155,45,127,0.8)", borderRadius:7, padding:"6px 13px", cursor:"pointer", ...FONT.smallBold },
  removeBtn:{ background:"transparent", border:`1px solid rgba(248,113,113,0.25)`, color:"rgba(248,113,113,0.6)", borderRadius:6, padding:"4px 9px", cursor:"pointer", ...FONT.tiny },
  totalBox:{ background:"rgba(155,45,127,0.07)", border:`1px solid rgba(155,45,127,0.2)`, borderRadius:10, padding:"12px 18px", textAlign:"right", marginTop:14 },
  totalLabel:{ ...FONT.tinyBold, letterSpacing:"2px", textTransform:"uppercase", color:"rgba(155,45,127,0.65)", marginBottom:4 },
  totalValue:{ ...FONT.mono, fontSize:20, color:C.pink },
  divider:{ borderTop:`1px solid ${C.border}`, margin:"18px 0" },
  pill:{ display:"inline-block", padding:"2px 8px", borderRadius:20, ...FONT.tinyBold, letterSpacing:"1px" },
});

const segColor = {
  "Curtain/Sheer": {bg:"rgba(96,165,250,0.1)", color:"#60a5fa", border:"rgba(96,165,250,0.2)"},
  "Roman":         {bg:"rgba(52,211,153,0.1)",  color:"#34d399", border:"rgba(52,211,153,0.2)"},
  "Readymade":     {bg:"rgba(155,45,127,0.1)",  color:"#9b2d7f", border:"rgba(155,45,127,0.2)"},
};

// ─── QUOTE COUNTER ────────────────────────────────────────────────────────────
// ─── HELPERS ──────────────────────────────────────────────────────────────────
const uid = () => crypto.randomUUID();
const fmt = (n) => (parseFloat(n)||0).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2});
const ceilVal = (n) => Math.ceil(parseFloat(n)||0);
const roundPanels = (n) => { const f=Math.floor(n); return (n-f)>0.6 ? f+1 : f; };
const ceilToQuarter = (n) => Math.ceil(n*4)/4;
const roundToNearest500 = (n) => Math.round(n/500)*500;
const getSegment = (type) => { for(const [seg,types] of Object.entries(CURTAIN_SEGMENT)){ if(types.find(t=>t.type===type)) return seg; } return "Curtain/Sheer"; };
const getAllTypesForSegment = (seg, customCurtainTypes) => [...(CURTAIN_SEGMENT[seg]||[]), ...(customCurtainTypes[seg]||[]).map(t=>({type:t,stitchRate:0}))];

const calcCS = (p) => {
  const H=parseFloat(p.height)||0, W=parseFloat(p.width)||0;
  const panels=roundPanels(W/20), mpp=ceilToQuarter((H+12)/39), totalMeter=panels*mpp;
  const fabricAmt=totalMeter*(parseFloat(p.mrp)||0);
  const discAmt=fabricAmt*(parseFloat(p.discount)||0)/100;
  return { panels, mpp, totalMeter, fabricAmt, discAmt, amount:fabricAmt-discAmt };
};
const calcRoman = (p) => {
  const H=parseFloat(p.height)||0, W=parseFloat(p.width)||0;
  const qty=ceilToQuarter((H+10)/39), sqft=Math.ceil((H*W)/144);
  const fabricAmt=qty*(parseFloat(p.mrp)||0);
  const discAmt=fabricAmt*(parseFloat(p.discount)||0)/100;
  return { qty, sqft, fabricAmt, discAmt, amount:fabricAmt-discAmt };
};
const calcRM = (p) => {
  const H=parseFloat(p.height)||0, W=parseFloat(p.width)||0;
  const sqin=H*W;
  const sqft=parseFloat((sqin/144).toFixed(3));
  const sqm=parseFloat((sqin/1550).toFixed(3));
  const areaUnit=p.areaUnit||"sqft";
  const computed=areaUnit==="sqm"?sqm:sqft;
  const fabricAmt=computed*(parseFloat(p.rate)||0);
  const discAmt=fabricAmt*(parseFloat(p.discount)||0)/100;
  const amount=fabricAmt-discAmt;
  return { sqin, sqft, sqm, computed, amount, areaUnit };
};
const getPanelAmount = (p) => {
  const seg=getSegment(p.curtainType);
  if(seg==="Curtain/Sheer") return calcCS(p).amount;
  if(seg==="Roman") return calcRoman(p).amount;
  return calcRM(p).amount;
};
const calcHW = (hw) => {
  if(hw.category==="Roman DLX") return ceilVal((parseFloat(hw.width)||0)/12)*(parseFloat(hw.rate)||0);
  if(CURTAIN_ROD_OPTIONS.includes(hw.category)) return (parseFloat(hw.qty)||0)*(parseFloat(hw.price)||0);
  const mrp=parseFloat(hw.mrp)||0;
  return (parseFloat(hw.qty)||0)*(mrp - mrp*(parseFloat(hw.discount)||0)/100);
};
const roomTotal = (room) => {
  let fabricTotal=0, totalCSPanels=0, totalRomanSqft=0, hardwareTotal=0;
  room.windows.forEach(win => {
    win.panels.forEach(p => {
      const seg=getSegment(p.curtainType);
      fabricTotal+=getPanelAmount(p);
      if(seg==="Curtain/Sheer"){ const c=calcCS(p); totalCSPanels+=c.panels; }
      if(seg==="Roman"){ const c=calcRoman(p); totalRomanSqft+=c.sqft; }
    });
    const e=win.extras;
    fabricTotal+=(parseFloat(e.trimQty)||0)*(parseFloat(e.trimMrp)||0);
    fabricTotal+=(parseFloat(e.borderQty)||0)*(parseFloat(e.borderMrp)||0);
    fabricTotal+=(parseFloat(e.liningBQty)||0)*(parseFloat(e.liningBMrp)||0);
    fabricTotal+=(parseFloat(e.liningNQty)||0)*(parseFloat(e.liningNMrp)||0);
  });
  room.hardware.forEach(hw => { hardwareTotal+=calcHW(hw); });
  const st=room.stitching||{curtainSheerRate:300,romanRate:115};
  const curtainStitch=totalCSPanels*(parseFloat(st.curtainSheerRate)||0);
  const romanStitch=totalRomanSqft*(parseFloat(st.romanRate)||0);
  const stitchTotal=curtainStitch+romanStitch;
  const total=fabricTotal+stitchTotal+hardwareTotal;
  return { fabricTotal, stitchTotal, hardwareTotal, total };
};
const grandTotal = (rooms) => rooms.reduce((s,r)=>s+roomTotal(r).total,0);
const totalWins = (rooms) => rooms.reduce((s,r)=>s+r.windows.length,0);

const newPanel = (seg="Curtain/Sheer") => {
  const ft=CURTAIN_SEGMENT[seg][0];
  return { id:uid(), curtainType:ft.type, height:"", width:"", mfr:"Nu Home", catalogue:"", snDesign:"", mrp:"", discount:15, rate:"", label:seg==="Curtain/Sheer"?"Main":seg, ...(seg==="Readymade"?{areaUnit:"sqft"}:{}) };
};
const newExtras = () => ({ trimQty:"",trimMrp:"",borderQty:"",borderMrp:"",liningBQty:"",liningBMrp:"",liningNQty:"",liningNMrp:"" });
const newWindow = (n) => ({ id:uid(), name:`Window ${n}`, panels:[newPanel()], extras:newExtras() });
const newRoom = (floor="GF",roomType="LIVING") => ({ id:uid(), roomType, floor, windows:[newWindow(1)], hardware:[], notes:"", stitching:{ curtainSheerRate:300, romanRate:115 } });

const toWords = (n) => {
  if(!n) return "Zero";
  const ones=["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine","Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen","Seventeen","Eighteen","Nineteen"];
  const tens=["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  const conv=(x)=>{ if(x===0)return""; if(x<20)return ones[x]+" "; if(x<100)return tens[Math.floor(x/10)]+(x%10?" "+ones[x%10]:"")+" "; if(x<1000)return ones[Math.floor(x/100)]+" Hundred "+conv(x%100); if(x<100000)return conv(Math.floor(x/1000))+"Thousand "+conv(x%1000); if(x<10000000)return conv(Math.floor(x/100000))+"Lakh "+conv(x%100000); return conv(Math.floor(x/10000000))+"Crore "+conv(x%10000000); };
  return "Rupees "+conv(Math.round(n)).trim()+" Only";
};

// ─── AUTH ─────────────────────────────────────────────────────────────────────
function useAuth() {
  const [session, setSession] = useState(undefined); // undefined = loading, null = logged out
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);
  const login = async (email, pw) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    return error ? error.message : null;
  };
  const logout = () => { resetTenantCache(); return supabase.auth.signOut(); };
  return { session, login, logout };
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const attempt = async () => {
    setLoading(true); setError(null);
    const err = await onLogin(email, pw);
    if (err) { setError(err); setLoading(false); }
  };
  const inputStyle = (hasError) => ({width:"100%",background:"#f0eaf0",border:`1.5px solid ${hasError?"#dc2626":"#ddd0dc"}`,borderRadius:8,padding:"11px 14px",color:"#1a1014",fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:8,transition:"border-color 0.15s"});
  return (
    <div style={{minHeight:"100vh",background:"#f5f0f4",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Segoe UI',system-ui,sans-serif"}}>
      <div style={{background:"#ffffff",border:"1px solid #ddd0dc",borderRadius:16,padding:40,width:340,textAlign:"center",boxShadow:"0 8px 40px rgba(155,45,127,0.12)"}}>
        <img src={drapeiqLogo} alt="DrapeIQ" style={{height:120,objectFit:"contain",display:"block",margin:"0 auto 12px"}}/>
        <div style={{...FONT.tinyBold,letterSpacing:"3px",color:"#8a7088",marginBottom:28,textTransform:"uppercase"}}>Quotation System</div>
        <input type="email" value={email}
          onChange={e=>{setEmail(e.target.value);setError(null);}}
          onKeyDown={e=>e.key==="Enter"&&attempt()}
          placeholder="Email address"
          style={inputStyle(false)}/>
        <input type="password" value={pw}
          onChange={e=>{setPw(e.target.value);setError(null);}}
          onKeyDown={e=>e.key==="Enter"&&attempt()}
          placeholder="Password"
          style={inputStyle(!!error)}/>
        {error && <div style={{color:"#dc2626",...FONT.small,marginBottom:8}}>{error}</div>}
        <button onClick={attempt} disabled={loading}
          style={{width:"100%",background:"#9b2d7f",color:"white",border:"none",borderRadius:8,padding:"12px",...FONT.bodyMed,fontWeight:700,cursor:loading?"not-allowed":"pointer",marginTop:6,letterSpacing:"0.3px",opacity:loading?0.7:1}}>
          {loading ? "Signing in…" : "Sign In →"}
        </button>
        <div style={{fontSize:10,letterSpacing:"2px",color:"#8a7088",textTransform:"uppercase",marginTop:24,textAlign:"center"}}>A product of The Dino Creatives</div>
      </div>
    </div>
  );
}

// ─── UI PRIMITIVES ────────────────────────────────────────────────────────────
const Field = ({label,value,onChange,type="text",placeholder="",C,s,readOnly=false}) => (
  <div>
    {label && <label style={s.fieldLabel}>{label}</label>}
    <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder} readOnly={readOnly}
      style={{...s.input,...(readOnly?{color:C.textDim,fontStyle:"italic",cursor:"default",userSelect:"none"}:{})}}
      onFocus={e=>{if(!readOnly) e.target.style.borderColor=C.pink}}
      onBlur={e=>e.target.style.borderColor=C.border}/>
  </div>
);

const SI = ({value,onChange,placeholder="",width=70,align="center",type="number",C,s}) => (
  <input type={type} value={value||""} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{...s.smallInput,width,textAlign:align}}
    onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}/>
);

const SISel = ({value,onChange,options,width=130,s}) => (
  <select value={value||""} onChange={e=>onChange(e.target.value)}
    style={{...s.smallInput,width,textAlign:"left",cursor:"pointer"}}>
    {options.map(o=><option key={o.value||o} value={o.value||o}>{o.label||o}</option>)}
  </select>
);

// ─── CUSTOM-EXTENSIBLE DROPDOWN ───────────────────────────────────────────────
// Used for Room Type and Floor — shows existing options + "Custom..." entry
const ExtSelect = ({value, onChange, options, width="100%", s, C, placeholder="Select..."}) => {
  const [adding, setAdding] = useState(false);
  const [nv, setNv] = useState("");
  if(adding) return (
    <div style={{display:"flex",gap:6,alignItems:"center"}}>
      <input autoFocus value={nv} onChange={e=>setNv(e.target.value.toUpperCase())}
        placeholder="e.g. UTILITY"
        style={{...s.input,margin:0,flex:1}}
        onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}
        onKeyDown={e=>{if(e.key==="Enter"&&nv.trim()){onChange(nv.trim());setAdding(false);setNv("");}}}/>
      <button onClick={()=>{if(nv.trim()){onChange(nv.trim());setAdding(false);setNv("");}}} style={{...s.addBtn,padding:"7px 10px"}}>✓</button>
      <button onClick={()=>{setAdding(false);setNv("");}} style={{...s.removeBtn,padding:"5px 9px"}}>✕</button>
    </div>
  );
  return (
    <select value={value||""} onChange={e=>{ if(e.target.value==="__custom__") setAdding(true); else onChange(e.target.value); }}
      style={{...s.select,width}}>
      {options.map(o=><option key={o} value={o}>{o}</option>)}
      <option value="__custom__">＋ Add custom…</option>
    </select>
  );
};

// ─── MANUFACTURER SELECTOR ────────────────────────────────────────────────────
const MfrSelector = ({value,onChange,isRoman,extraMfrs,onAddMfr,C,s}) => {
  const [adding,setAdding]=useState(false);
  const [nv,setNv]=useState("");
  const all=[...ALL_MANUFACTURERS,...extraMfrs.filter(m=>!ALL_MANUFACTURERS.includes(m))];
  return (
    <div style={{display:"flex",gap:4,alignItems:"center"}}>
      {adding ? (
        <>
          <input autoFocus value={nv} onChange={e=>setNv(e.target.value)} placeholder="Manufacturer"
            style={{...s.smallInput,width:118}} onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}/>
          <button onClick={()=>{if(nv.trim()){onAddMfr(nv.trim());onChange(nv.trim());setNv("");setAdding(false);}}} style={{...s.addBtn,padding:"4px 7px",...FONT.tiny}}>✓</button>
          <button onClick={()=>{setAdding(false);setNv("");}} style={{...s.removeBtn,padding:"4px 7px"}}>✕</button>
        </>
      ):(
        <>
          <select value={value||""} onChange={e=>onChange(e.target.value)} style={{...s.smallInput,width:118,cursor:"pointer"}}>
            {all.map(m=><option key={m}>{m}</option>)}
          </select>
          <button onClick={()=>setAdding(true)} style={{...s.addBtn,padding:"3px 6px",...FONT.tiny}} title="Add manufacturer">+</button>
        </>
      )}
    </div>
  );
};

// ─── PANEL ROW ────────────────────────────────────────────────────────────────
const PanelRow = ({panel,onUpdate,onDelete,showDelete,extraMfrs,onAddMfr,customCurtainTypes,onAddCustomType,C,s}) => {
  const seg=getSegment(panel.curtainType);
  const sc=segColor[seg];
  const [addingCustomType,setAddingCustomType]=useState(false);
  const [newCustomType,setNewCustomType]=useState("");
  const handleType=(type)=>{
    if(type==="__add_custom__"){ setAddingCustomType(true); return; }
    const ns=getSegment(type);
    const mfr=ns==="Roman"?"Nu Home":panel.mfr;
    onUpdate({...panel,curtainType:type,mfr});
  };
  const confirmCustomType=()=>{
    const name=newCustomType.trim(); if(!name) return;
    onAddCustomType(seg,name);
    onUpdate({...panel,curtainType:name});
    setNewCustomType(""); setAddingCustomType(false);
  };
  let calc={};
  if(seg==="Curtain/Sheer") calc=calcCS(panel);
  if(seg==="Roman") calc=calcRoman(panel);
  if(seg==="Readymade") calc=calcRM(panel);
  return (
    <tr style={{background:C.panelRowBg}}>
      <td style={s.td}>
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <input value={panel.label||""} onChange={e=>onUpdate({...panel,label:e.target.value})}
            style={{background:"transparent",border:"none",borderBottom:`1px solid ${C.border}`,color:C.text,...FONT.small,width:48,outline:"none"}}/>
          {showDelete && <button onClick={onDelete} style={{background:"none",border:"none",color:"rgba(248,113,113,0.45)",cursor:"pointer",fontSize:12,padding:0}}>✕</button>}
        </div>
      </td>
      <td style={s.td}>
        <span style={{...s.pill,background:sc.bg,color:sc.color,border:`1px solid ${sc.border}`,marginBottom:3,display:"block",textAlign:"center"}}>{seg}</span>
        {addingCustomType?(
          <div style={{display:"flex",gap:4,alignItems:"center"}}>
            <input autoFocus value={newCustomType} onChange={e=>setNewCustomType(e.target.value)}
              placeholder="Type name" style={{...s.smallInput,width:110}}
              onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}
              onKeyDown={e=>{if(e.key==="Enter") confirmCustomType(); if(e.key==="Escape"){setAddingCustomType(false);setNewCustomType("");}}}/>
            <button onClick={confirmCustomType} style={{...s.addBtn,padding:"4px 7px",...FONT.tiny}}>✓</button>
            <button onClick={()=>{setAddingCustomType(false);setNewCustomType("");}} style={{...s.removeBtn,padding:"4px 7px"}}>✕</button>
          </div>
        ):(
          <select value={panel.curtainType} onChange={e=>handleType(e.target.value)}
            style={{...s.smallInput,width:162,cursor:"pointer"}}>
            {Object.entries(CURTAIN_SEGMENT).map(([sn])=>(
              <optgroup key={sn} label={`─── ${sn} ───`}>
                {getAllTypesForSegment(sn,customCurtainTypes).map(t=><option key={t.type}>{t.type}</option>)}
              </optgroup>
            ))}
            <option value="__add_custom__">＋ Add custom type…</option>
          </select>
        )}
      </td>
      <td style={s.td}><SI value={panel.height} onChange={v=>onUpdate({...panel,height:v})} placeholder="H" width={54} C={C} s={s}/></td>
      <td style={s.td}><SI value={panel.width} onChange={v=>onUpdate({...panel,width:v})} placeholder="W" width={54} C={C} s={s}/></td>
      <td style={{...s.td,fontFamily:"monospace",...FONT.tiny,minWidth:110}}>
        {seg==="Curtain/Sheer"&&calc.totalMeter>0&&(<div><div style={{color:C.pinkLight,fontWeight:700}}>{calc.totalMeter.toFixed(2)} m</div><div style={{color:C.textDim}}>{calc.panels.toFixed(2)}p × {calc.mpp.toFixed(2)}m</div></div>)}
        {seg==="Roman"&&calc.qty>0&&(<div><div style={{color:C.pinkLight,fontWeight:700}}>{calc.qty.toFixed(2)} units</div><div style={{color:C.textDim}}>{calc.sqft?.toFixed(2)} sqft</div></div>)}
        {seg==="Readymade"&&calc.sqin>0&&(<div>
          <div style={{color:C.pinkLight,fontWeight:700}}>{calc.sqft.toFixed(3)} sqft</div>
          <div style={{color:C.textDim}}>{calc.sqm.toFixed(3)} sqm</div>
        </div>)}
        {!calc.amount&&<span style={{color:C.textDim}}>—</span>}
      </td>
      <td style={s.td}><MfrSelector value={panel.mfr} onChange={v=>onUpdate({...panel,mfr:v})} isRoman={seg==="Roman"} extraMfrs={extraMfrs} onAddMfr={onAddMfr} C={C} s={s}/></td>
      <td style={s.td}><SI value={panel.catalogue} onChange={v=>onUpdate({...panel,catalogue:v})} placeholder="Catalogue" width={86} type="text" align="left" C={C} s={s}/></td>
      <td style={s.td}><SI value={panel.snDesign} onChange={v=>onUpdate({...panel,snDesign:v})} placeholder="SN/Design" width={76} type="text" align="left" C={C} s={s}/></td>
      {seg!=="Readymade"?(
        <td style={s.td}><SI value={panel.mrp} onChange={v=>onUpdate({...panel,mrp:v})} placeholder="MRP/m" width={68} align="right" C={C} s={s}/></td>
      ):(
        <td style={s.td}>
          <div style={{display:"flex",gap:3,marginBottom:4}}>
            {["sqft","sqm"].map(u=>(
              <button key={u} onClick={()=>onUpdate({...panel,areaUnit:u,rate:""})}
                style={{background:(panel.areaUnit||"sqft")===u?C.pink:"transparent",color:(panel.areaUnit||"sqft")===u?"#fff":C.textMuted,border:`1px solid ${(panel.areaUnit||"sqft")===u?C.pink:C.border}`,borderRadius:4,padding:"1px 5px",...FONT.tinyBold,cursor:"pointer"}}>
                {u}
              </button>
            ))}
          </div>
          <SI value={panel.rate} onChange={v=>onUpdate({...panel,rate:v})} placeholder="Rate" width={68} align="right" C={C} s={s}/>
        </td>
      )}
      <td style={s.td}>
          <div style={{display:"flex",alignItems:"center",gap:3}}>
          <SI value={panel.discount} onChange={v=>onUpdate({...panel,discount:v})} width={58} C={C} s={s}/>
            <span style={{color:C.textDim,...FONT.small}}>%</span>
          </div>
        </td>
      <td style={s.tdAmt}>{calc.amount>0?`₹${fmt(calc.amount)}`:"—"}</td>
    </tr>
  );
};

// ─── WINDOW EDITOR ────────────────────────────────────────────────────────────
const WindowEditor = ({win,onUpdate,onDelete,showDelete,extraMfrs,onAddMfr,customCurtainTypes,onAddCustomType,C,s}) => {
  const addPanel=(seg)=>onUpdate({...win,panels:[...win.panels,newPanel(seg)]});
  const updPanel=(id,p)=>onUpdate({...win,panels:win.panels.map(x=>x.id===id?p:x)});
  const delPanel=(id)=>onUpdate({...win,panels:win.panels.filter(x=>x.id!==id)});
  const updEx=(k,v)=>onUpdate({...win,extras:{...win.extras,[k]:v}});
  const e=win.extras;
  return (
    <div style={s.winWrapper}>
      <div style={s.winHeader}>
        <input value={win.name} onChange={ev=>onUpdate({...win,name:ev.target.value})}
          style={{background:"transparent",border:"none",borderBottom:`1px solid rgba(155,45,127,0.3)`,color:C.pinkLight,...FONT.bodyMed,outline:"none",width:128}}/>
        <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
          {Object.keys(CURTAIN_SEGMENT).map(seg=>(
            <button key={seg} onClick={()=>addPanel(seg)}
              style={{...s.addBtn,...FONT.tiny,padding:"4px 9px",color:segColor[seg].color,borderColor:segColor[seg].border,background:segColor[seg].bg}}>
              + {seg}
            </button>
          ))}
          {showDelete&&<button onClick={onDelete} style={s.removeBtn}>Remove Window</button>}
        </div>
      </div>
      <div style={{overflowX:"auto",background:C.bg2}}>
        <table style={{width:"100%",borderCollapse:"collapse",...FONT.small,minWidth:1080}}>
          <thead>
            <tr>{["Panel","Type","H(in)","W(in)","Computed","Manufacturer","Catalogue","SN/Design","MRP / Rate","Disc%","Amount"].map(h=>(
              <th key={h} style={{...s.th,textAlign:h==="Amount"?"right":"left"}}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {win.panels.map(p=>(
              <PanelRow key={p.id} panel={p} onUpdate={np=>updPanel(p.id,np)} onDelete={()=>delPanel(p.id)}
                showDelete={win.panels.length>1} extraMfrs={extraMfrs} onAddMfr={onAddMfr}
                customCurtainTypes={customCurtainTypes} onAddCustomType={onAddCustomType} C={C} s={s}/>
            ))}
          </tbody>
        </table>
      </div>
      {/* Window Extras — narrower qty boxes so MRP/Amount have room */}
      <div style={{padding:"10px 14px",borderTop:`1px solid rgba(155,45,127,0.1)`,background:C.winExtraBg}}>
        <div style={{...FONT.tinyBold,letterSpacing:"2px",color:C.textDim,marginBottom:8,textTransform:"uppercase"}}>Window Extras — No Discount</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
          {[["trim","Trim"],["border","Border"],["liningB","Lining (Blackout)"],["liningN","Lining (Normal)"]].map(([k,label])=>{
            const qty=parseFloat(e[`${k}Qty`])||0, mrp=parseFloat(e[`${k}Mrp`])||0, amt=qty*mrp;
            return (
              <div key={k} style={{background:C.bg2,borderRadius:8,padding:"9px 10px",border:`1px solid ${C.border}`}}>
                <div style={{...FONT.smallBold,color:C.textMuted,marginBottom:7}}>{label}</div>
                <div style={{display:"flex",gap:5,alignItems:"center"}}>
                  {/* Qty — narrow fixed width */}
                  <div style={{flexShrink:0}}>
                    <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Qty(m)</div>
                    <SI value={e[`${k}Qty`]} onChange={v=>updEx(`${k}Qty`,v)} width={46} align="center" C={C} s={s}/>
                  </div>
                  {/* MRP — flex grows */}
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>MRP/m</div>
                    <SI value={e[`${k}Mrp`]} onChange={v=>updEx(`${k}Mrp`,v)} width="100%" align="right" C={C} s={s}/>
                  </div>
                  {/* Amount */}
                  <div style={{flexShrink:0,textAlign:"right"}}>
                    <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Amt</div>
                    <div style={{...FONT.mono,color:C.pink,fontSize:11,whiteSpace:"nowrap"}}>{amt>0?`₹${fmt(amt)}`:"—"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─── HARDWARE SECTION ─────────────────────────────────────────────────────────
const HardwareSection = ({hardware,onUpdate,extraAccessories,onAddAccessory,extraRodTypes,onAddRodType,C,s}) => {
  const [addingAcc,setAddingAcc]=useState(false);
  const [newAcc,setNewAcc]=useState("");
  const [addingRod,setAddingRod]=useState(false);
  const [newRodType,setNewRodType]=useState("");
  const allAcc=[...ACCESSORY_OPTIONS,...extraAccessories];
  const allRodTypes=[...CURTAIN_ROD_OPTIONS,...extraRodTypes];
  const addHw=(type)=>{
    let item;
    if(type==="roman") item={id:uid(),category:"Roman DLX",width:"",rate:""};
    else if(type==="rod") item={id:uid(),category:"Curtain Rod (SS)",qty:"",price:""};
    else item={id:uid(),category:allAcc[0]||"Finials",qty:"",mrp:"",discount:0};
    onUpdate([...hardware,item]);
  };
  const updHw=(id,hw)=>onUpdate(hardware.map(h=>h.id===id?hw:h));
  const delHw=(id)=>onUpdate(hardware.filter(h=>h.id!==id));
  return (
    <div style={{marginTop:18}}>
      <div style={{borderTop:`1px solid ${C.border}`,marginBottom:14}}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,flexWrap:"wrap",gap:8}}>
        <span style={{...FONT.sectionLabel,color:C.textMuted}}>Hardware (Room-Level)</span>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          <button onClick={()=>addHw("roman")} style={{...s.addBtn,...FONT.tiny,padding:"4px 10px",color:segColor["Roman"].color,borderColor:segColor["Roman"].border,background:segColor["Roman"].bg}}>+ Roman DLX Channel</button>
          <button onClick={()=>addHw("rod")} style={{...s.addBtn,...FONT.tiny,padding:"4px 10px",color:segColor["Curtain/Sheer"].color,borderColor:segColor["Curtain/Sheer"].border,background:segColor["Curtain/Sheer"].bg}}>+ Curtain Rod / M-Track</button>
          <button onClick={()=>addHw("acc")} style={{...s.addBtn,...FONT.tiny,padding:"4px 10px"}}>+ Accessory</button>
        </div>
      </div>
      {hardware.length===0&&<div style={{color:C.textDim,...FONT.small,fontStyle:"italic",paddingBottom:6}}>No hardware items added.</div>}
      {hardware.map(hw=>{
        const amt=calcHW(hw);
        if(hw.category==="Roman DLX"){
          const rf=ceilVal((parseFloat(hw.width)||0)/12);
          return (
            <div key={hw.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:C.bg3,borderRadius:8,padding:"9px 12px",border:`1px solid rgba(52,211,153,0.15)`,flexWrap:"wrap"}}>
              <span style={{...s.pill,background:"rgba(52,211,153,0.1)",color:"#34d399",border:"1px solid rgba(52,211,153,0.2)",whiteSpace:"nowrap"}}>Roman DLX Channel</span>
              <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Width (in)</div><SI value={hw.width} onChange={v=>updHw(hw.id,{...hw,width:v})} width={76} placeholder="Width" C={C} s={s}/></div>
              {rf>0&&<div style={{color:C.textDim,...FONT.small}}>→ RF: <span style={{color:C.pinkLight,fontWeight:700}}>{rf} rf</span> ⌈ceil⌉</div>}
              <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Rate/rf (₹)</div><SI value={hw.rate} onChange={v=>updHw(hw.id,{...hw,rate:v})} width={76} align="right" C={C} s={s}/></div>
              <div style={{...FONT.mono,color:C.pink,fontSize:13,minWidth:80,textAlign:"right"}}>{amt>0?`₹${fmt(amt)}`:"—"}</div>
              <button onClick={()=>delHw(hw.id)} style={s.removeBtn}>✕ Remove</button>
            </div>
          );
        }
        if(allRodTypes.includes(hw.category)){
          return (
            <div key={hw.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:C.bg3,borderRadius:8,padding:"9px 12px",border:`1px solid rgba(96,165,250,0.15)`,flexWrap:"wrap"}}>
              <div style={{display:"flex",gap:4,alignItems:"center"}}>
                <SISel value={hw.category} onChange={v=>updHw(hw.id,{...hw,category:v})} options={allRodTypes} width={152} s={s}/>
                <button onClick={()=>setAddingRod(true)} style={{...s.addBtn,padding:"3px 6px",...FONT.tiny}} title="Add rod/track type">+</button>
              </div>
              <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Qty</div><SI value={hw.qty} onChange={v=>updHw(hw.id,{...hw,qty:v})} width={76} C={C} s={s}/></div>
              <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Price/unit (₹)</div><SI value={hw.price} onChange={v=>updHw(hw.id,{...hw,price:v})} width={86} align="right" C={C} s={s}/></div>
              <div style={{...FONT.mono,color:C.pink,fontSize:13,minWidth:80,textAlign:"right"}}>{amt>0?`₹${fmt(amt)}`:"—"}</div>
              <button onClick={()=>delHw(hw.id)} style={s.removeBtn}>✕ Remove</button>
            </div>
          );
        }
        return (
          <div key={hw.id} style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,background:C.bg3,borderRadius:8,padding:"9px 12px",border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
            <SISel value={hw.category} onChange={v=>updHw(hw.id,{...hw,category:v})} options={allAcc} width={150} s={s}/>
            <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Qty</div><SI value={hw.qty} onChange={v=>updHw(hw.id,{...hw,qty:v})} width={66} C={C} s={s}/></div>
            <div><div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>MRP (₹)</div><SI value={hw.mrp} onChange={v=>updHw(hw.id,{...hw,mrp:v})} width={76} align="right" C={C} s={s}/></div>
            <div>
              <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Disc%</div>
              <div style={{display:"flex",alignItems:"center",gap:3}}>
                <SI value={hw.discount} onChange={v=>updHw(hw.id,{...hw,discount:v})} width={46} C={C} s={s}/>
                <span style={{color:C.textDim,...FONT.small}}>%</span>
              </div>
            </div>
            <div style={{...FONT.mono,color:C.pink,fontSize:13,minWidth:80,textAlign:"right"}}>{amt>0?`₹${fmt(amt)}`:"—"}</div>
            <button onClick={()=>delHw(hw.id)} style={s.removeBtn}>✕ Remove</button>
          </div>
        );
      })}
      {addingRod&&(
        <div style={{display:"flex",gap:7,alignItems:"center",marginTop:6}}>
          <input autoFocus value={newRodType} onChange={e=>setNewRodType(e.target.value)} placeholder="New rod/track type"
            style={{...s.input,maxWidth:250,margin:0}} onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}
            onKeyDown={e=>{if(e.key==="Enter"&&newRodType.trim()){onAddRodType(newRodType.trim());setNewRodType("");setAddingRod(false);}}}/>
          <button onClick={()=>{if(newRodType.trim()){onAddRodType(newRodType.trim());setNewRodType("");setAddingRod(false);}}} style={s.addBtn}>Add</button>
          <button onClick={()=>{setAddingRod(false);setNewRodType("");}} style={s.removeBtn}>Cancel</button>
        </div>
      )}
      {addingAcc?(
        <div style={{display:"flex",gap:7,alignItems:"center",marginTop:6}}>
          <input autoFocus value={newAcc} onChange={e=>setNewAcc(e.target.value)} placeholder="New accessory name"
            style={{...s.input,maxWidth:250,margin:0}} onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}/>
          <button onClick={()=>{if(newAcc.trim()){onAddAccessory(newAcc.trim());setNewAcc("");setAddingAcc(false);}}} style={s.addBtn}>Add</button>
          <button onClick={()=>setAddingAcc(false)} style={s.removeBtn}>Cancel</button>
        </div>
      ):(
        <button onClick={()=>setAddingAcc(true)} style={{...s.addBtn,...FONT.tiny,marginTop:4,borderStyle:"dashed"}}>+ Add new accessory type</button>
      )}
    </div>
  );
};

// ─── ROOM EDITOR (rendered inside floor tab) ──────────────────────────────────
const RoomEditor = ({room,onChange,onDelete,extraMfrs,onAddMfr,extraAccessories,onAddAccessory,extraRodTypes,onAddRodType,customCurtainTypes,onAddCustomType,C,s,allRoomTypes,allFloors}) => {
  const {fabricTotal,stitchTotal,hardwareTotal,total}=roomTotal(room);
  const st=room.stitching||{curtainSheerRate:300,romanRate:115};
  // Compute panel counts for display in the stitching section
  let totalCSPanels=0, totalRomanSqft=0;
  room.windows.forEach(win=>win.panels.forEach(p=>{
    const seg=getSegment(p.curtainType);
    if(seg==="Curtain/Sheer"){ totalCSPanels+=calcCS(p).panels; }
    if(seg==="Roman"){ totalRomanSqft+=calcRoman(p).sqft; }
  }));
  const hasCS=totalCSPanels>0, hasRoman=totalRomanSqft>0;
  const addWin=()=>onChange({...room,windows:[...room.windows,newWindow(room.windows.length+1)]});
  const updWin=(id,w)=>onChange({...room,windows:room.windows.map(x=>x.id===id?w:x)});
  const delWin=(id)=>onChange({...room,windows:room.windows.filter(x=>x.id!==id)});
  return (
    <div style={s.roomBody}>
      {/* Room settings row */}
      <div style={{display:"flex",gap:12,marginBottom:16,flexWrap:"wrap",alignItems:"flex-end"}}>
        <div style={{minWidth:160}}>
          <label style={s.fieldLabel}>Room Type</label>
          <ExtSelect value={room.roomType} onChange={v=>onChange({...room,roomType:v})} options={allRoomTypes} s={s} C={C}/>
        </div>
        <div style={{minWidth:130}}>
          <label style={s.fieldLabel}>Floor</label>
          <ExtSelect value={room.floor} onChange={v=>onChange({...room,floor:v})} options={allFloors} s={s} C={C}/>
        </div>
        <div style={{marginLeft:"auto"}}>
          <button onClick={onDelete} style={{...s.removeBtn,padding:"7px 14px",...FONT.small}}>🗑 Remove Room</button>
        </div>
      </div>
      {room.windows.map(win=>(
        <WindowEditor key={win.id} win={win}
          onUpdate={w=>updWin(win.id,w)}
          onDelete={()=>delWin(win.id)}
          showDelete={room.windows.length>1}
          extraMfrs={extraMfrs} onAddMfr={onAddMfr}
          customCurtainTypes={customCurtainTypes} onAddCustomType={onAddCustomType} C={C} s={s}/>
      ))}
      <button onClick={addWin} style={{...s.addBtn,marginBottom:16}}>+ Add Window</button>
      {/* ── Stitching section ── */}
      {(hasCS||hasRoman)&&(
        <div style={{border:`1px solid rgba(155,45,127,0.2)`,borderRadius:10,padding:"12px 16px",marginBottom:16,background:"rgba(155,45,127,0.03)"}}>
          <span style={{...FONT.sectionLabel,color:C.textMuted,display:"block",marginBottom:10}}>Stitching (Room Level)</span>
          <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
            {hasCS&&(
              <div style={{display:"flex",alignItems:"center",gap:10,background:C.bg2,borderRadius:8,padding:"8px 12px",border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                <div>
                  <div style={{...FONT.fieldLabel,color:C.textMuted,marginBottom:3}}>Curtain / Sheer</div>
                  <div style={{...FONT.tiny,color:C.textDim}}>Total panels: <span style={{color:C.pinkLight,fontWeight:700}}>{totalCSPanels.toFixed(2)}</span></div>
                </div>
                <div>
                  <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Rate / panel (₹)</div>
                  <SI value={st.curtainSheerRate} onChange={v=>onChange({...room,stitching:{...st,curtainSheerRate:v}})} width={72} align="right" C={C} s={s}/>
                </div>
                <div style={{...FONT.mono,color:C.pink,fontSize:13,minWidth:80,textAlign:"right"}}>
                  ₹{fmt(totalCSPanels*(parseFloat(st.curtainSheerRate)||0))}
                </div>
              </div>
            )}
            {hasRoman&&(
              <div style={{display:"flex",alignItems:"center",gap:10,background:C.bg2,borderRadius:8,padding:"8px 12px",border:`1px solid ${C.border}`,flexWrap:"wrap"}}>
                <div>
                  <div style={{...FONT.fieldLabel,color:C.textMuted,marginBottom:3}}>Roman</div>
                  <div style={{...FONT.tiny,color:C.textDim}}>Total sqft: <span style={{color:C.pinkLight,fontWeight:700}}>{totalRomanSqft}</span></div>
                </div>
                <div>
                  <div style={{...FONT.tiny,color:C.textDim,marginBottom:2}}>Rate / sqft (₹)</div>
                  <SI value={st.romanRate} onChange={v=>onChange({...room,stitching:{...st,romanRate:v}})} width={72} align="right" C={C} s={s}/>
                </div>
                <div style={{...FONT.mono,color:C.pink,fontSize:13,minWidth:80,textAlign:"right"}}>
                  ₹{fmt(totalRomanSqft*(parseFloat(st.romanRate)||0))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <HardwareSection hardware={room.hardware} onUpdate={hw=>onChange({...room,hardware:hw})} extraAccessories={extraAccessories} onAddAccessory={onAddAccessory} extraRodTypes={extraRodTypes} onAddRodType={onAddRodType} C={C} s={s}/>
      <div style={s.divider}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",gap:14,flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          <label style={s.fieldLabel}>Room Notes</label>
          <input value={room.notes||""} onChange={e=>onChange({...room,notes:e.target.value})} placeholder="Special instructions..."
            style={s.input} onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}/>
        </div>
        <div style={s.totalBox}>
          <div style={{...FONT.tiny,color:C.textDim,marginBottom:6,display:"flex",flexDirection:"column",gap:2,textAlign:"right"}}>
            <span>Fabric + Extras: <span style={{...FONT.mono,fontSize:12,color:C.textMuted}}>₹{fmt(fabricTotal)}</span></span>
            <span>Stitching + Hardware: <span style={{...FONT.mono,fontSize:12,color:C.textMuted}}>₹{fmt(stitchTotal+hardwareTotal)}</span></span>
          </div>
          <div style={s.totalLabel}>Room Total</div>
          <div style={s.totalValue}>₹{fmt(total)}</div>
        </div>
      </div>
    </div>
  );
};

// ─── FLOOR BLOCK (floor heading + room tabs) ──────────────────────────────────
const FloorBlock = ({floor,rooms,onChangeRoom,onDeleteRoom,onAddRoom,extraMfrs,onAddMfr,extraAccessories,onAddAccessory,extraRodTypes,onAddRodType,customCurtainTypes,onAddCustomType,C,s,allRoomTypes,allFloors}) => {
  const [activeTab, setActiveTab] = useState(0);
  // If active tab gets deleted, clamp
  const safeTab = Math.min(activeTab, rooms.length-1);
  const floorTotal = rooms.reduce((sum,r)=>sum+roomTotal(r).total,0);
  const floorWins = rooms.reduce((sum,r)=>sum+r.windows.length,0);
  return (
    <div style={s.floorBlock}>
      {/* Floor header bar */}
      <div style={s.floorHeader}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:34,height:34,borderRadius:8,background:"rgba(155,45,127,0.15)",border:`1px solid rgba(155,45,127,0.3)`,display:"flex",alignItems:"center",justifyContent:"center",...FONT.heading3,color:C.pink}}>{floor}</div>
          <div>
            <div style={{...FONT.heading2,color:C.pink}}>{floor} — {rooms.length} room{rooms.length!==1?"s":""} · {floorWins} window{floorWins!==1?"s":""}</div>
            <div style={{...FONT.tiny,color:C.textMuted,marginTop:2}}>Floor Total: <span style={{...FONT.mono,color:C.pink,fontSize:12}}>₹{fmt(floorTotal)}</span></div>
          </div>
        </div>
        <button onClick={onAddRoom} style={{...s.addBtn,padding:"7px 14px",...FONT.small}}>+ Add Room</button>
      </div>
      {/* Room tabs */}
      <div style={s.roomTabBar}>
        {rooms.map((room,i)=>(
          <button key={room.id} onClick={()=>setActiveTab(i)}
            style={i===safeTab ? s.roomTabActive : s.roomTabInactive}>
            {room.roomType}
            {i===safeTab && <span style={{marginLeft:5,opacity:0.7,...FONT.tiny}}>₹{fmt(roomTotal(room).total)}</span>}
          </button>
        ))}
      </div>
      {/* Active room content */}
      {rooms[safeTab] && (
        <RoomEditor
          room={rooms[safeTab]}
          onChange={r=>onChangeRoom(rooms[safeTab].id,r)}
          onDelete={()=>{ onDeleteRoom(rooms[safeTab].id); setActiveTab(Math.max(0,safeTab-1)); }}
          extraMfrs={extraMfrs} onAddMfr={onAddMfr}
          extraAccessories={extraAccessories} onAddAccessory={onAddAccessory}
          extraRodTypes={extraRodTypes} onAddRodType={onAddRodType}
          customCurtainTypes={customCurtainTypes} onAddCustomType={onAddCustomType}
          C={C} s={s} allRoomTypes={allRoomTypes} allFloors={allFloors}/>
      )}
    </div>
  );
};

// ─── FITTING SECTION ──────────────────────────────────────────────────────────
const FittingSection = ({fitting,onUpdate,windowCount,C,s}) => {
  const [custom,setCustom]=useState(false);
  const [cv,setCv]=useState("");
  const rate=parseFloat(fitting.rate)||0;
  const total=windowCount*rate;

  const handleSetCustom=()=>{
    onUpdate({...fitting,rate:cv});
    setCustom(false);
  };

  return (
    <div style={{...s.card,border:`1px solid rgba(155,45,127,0.2)`,background:"rgba(155,45,127,0.03)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:14}}>
        <div>
          <span style={{...FONT.sectionLabel,color:C.textMuted,display:"block",marginBottom:6}}>Fitting Charges</span>
          <div style={{...FONT.small,color:C.textMuted}}>Total windows: <span style={{color:C.pink,fontWeight:700}}>{windowCount}</span></div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap"}}>
          <div>
            <label style={s.fieldLabel}>Rate per Window</label>
            {custom?(
              <div style={{display:"flex",gap:7,alignItems:"center"}}>
                <input type="number" value={cv} onChange={e=>setCv(e.target.value)} placeholder="Enter rate"
                  style={{...s.input,width:140,margin:0}} onFocus={e=>e.target.style.borderColor=C.pink} onBlur={e=>e.target.style.borderColor=C.border}/>
                <button onClick={handleSetCustom} style={s.addBtn}>Set</button>
                <button onClick={()=>{setCustom(false);setCv("");}} style={s.removeBtn}>✕</button>
              </div>
            ):(
              <div style={{display:"flex",gap:7,alignItems:"center"}}>
                {/* FIX #1: select value always reads from fitting.rate so it stays in sync after custom set */}
                <select value={FITTING_RATES.includes(rate)?String(rate):"custom"}
                  onChange={e=>{ if(e.target.value==="custom"){setCv(String(rate));setCustom(true);} else onUpdate({...fitting,rate:e.target.value}); }}
                  style={{...s.select,width:200,margin:0}}>
                  {FITTING_RATES.map(r=><option key={r} value={String(r)}>₹{r} per window</option>)}
                  {!FITTING_RATES.includes(rate)&&<option value="custom">₹{rate} per window (custom)</option>}
                </select>
                <button onClick={()=>{setCv(String(rate));setCustom(true);}} style={{...s.addBtn,...FONT.tiny,padding:"5px 9px"}}>Custom</button>
              </div>
            )}
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{...FONT.tinyBold,color:C.textMuted,textTransform:"uppercase",letterSpacing:"1px",marginBottom:3}}>Fitting Total</div>
            <div style={{...FONT.mono,fontSize:20,color:C.pink}}>₹{fmt(total)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── PRINT PREVIEW ────────────────────────────────────────────────────────────
const PrintPreview = ({header,rooms,fitting,onClose,onQuoteConsumed,savedQuoteId,setSavedQuoteId,loadedQuote,hasChanges,existingQuoteNo,onRevisionSaved,toBePaidOverride,tenant}) => {
  const wc=totalWins(rooms);
  const fitTotal=wc*(parseFloat(fitting.rate)||0);
  const grand=grandTotal(rooms)+fitTotal;
  const toBePaid=toBePaidOverride !== undefined ? toBePaidOverride : roundToNearest500(grand*0.8);

  const [previewQuoteNo,setPreviewQuoteNo]=useState('DQ-...');
  const [printed,setPrinted]=useState(false);
  const [saving,setSaving]=useState(false);
  useEffect(()=>{
    if(existingQuoteNo){ setPreviewQuoteNo(existingQuoteNo); }
    else { peekNextQuoteNo().then(no=>setPreviewQuoteNo(no)).catch(()=>{}); }
  },[existingQuoteNo]);

  const ps={
    pth:{background:"#fdf5fc",padding:"8px 10px",textAlign:"left",fontWeight:700,borderBottom:"2px solid #e0d5de",color:"#333",fontSize:11,fontFamily:"'Segoe UI',sans-serif"},
    ptd:{padding:"6px 10px",borderBottom:"1px solid #f0e8ee",color:"#333",fontSize:12,fontFamily:"'Segoe UI',sans-serif"},
    ptdR:{padding:"6px 10px",borderBottom:"1px solid #f0e8ee",textAlign:"right",fontFamily:"'Courier New',monospace",color:"#333",fontSize:12},
    ptdA:{padding:"6px 10px",borderBottom:"1px solid #f0e8ee",textAlign:"right",fontFamily:"'Courier New',monospace",fontWeight:700,color:"#9b2d7f",fontSize:12},
  };

  const renderPanel=(win,p)=>{
    const seg=getSegment(p.curtainType);
    let calc={};
    if(seg==="Curtain/Sheer") calc=calcCS(p);
    if(seg==="Roman") calc=calcRoman(p);
    if(seg==="Readymade") calc=calcRM(p);
    if(!calc.amount||calc.amount<=0) return null;
    const desc=`${win.name} · ${p.label} (${p.curtainType})`;
    let q="—",r="—";
    if(seg==="Curtain/Sheer"){q=`${calc.totalMeter?.toFixed(2)} m`;r=`₹${fmt(p.mrp)}/m`;}
    if(seg==="Roman"){q=`${calc.qty?.toFixed(2)} units`;r=`₹${fmt(p.mrp)}/m`;}
    if(seg==="Readymade"){q=`${calc.computed?.toFixed(3)} ${calc.areaUnit||"sqft"}`;r=`₹${fmt(p.rate)}/${calc.areaUnit||"sqft"}`;}
    return <tr key={p.id}><td style={ps.ptd}>{desc}</td><td style={ps.ptdR}>{q}</td><td style={ps.ptdR}>{r}</td><td style={ps.ptdA}>₹{fmt(calc.amount)}</td></tr>;
  };

  const handlePrint=async()=>{
    const w=window.open("","_blank","width=1000,height=800");
    if(!w){ alert('Please allow popups for this site and try again.'); return; }
    w.document.write(`<!DOCTYPE html><html><head><style>body{display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:'Segoe UI',sans-serif;color:#9b2d7f;font-size:15px;}</style></head><body>Preparing document…</body></html>`);
    w.document.close();

    let effectiveQuoteNo = previewQuoteNo;
    setSaving(true);
    try{
      const needsSave = loadedQuote ? hasChanges : savedQuoteId === null;
      if(needsSave){
        let result;
        const currentAppState={header,rooms,fitting};
        if(loadedQuote && hasChanges){
          result=await saveRevision(currentAppState,grand,toBePaid,loadedQuote);
          if(result){
            onRevisionSaved(result);
            setPreviewQuoteNo(result.displayQuoteNo);
            effectiveQuoteNo=result.displayQuoteNo;
          }
        } else if(!loadedQuote){
          result=await saveNewQuote(currentAppState,grand,toBePaid);
          if(result){
            setSavedQuoteId(result.id);
            setPreviewQuoteNo(result.displayQuoteNo);
            effectiveQuoteNo=result.displayQuoteNo;
          }
        }
        if(result){ onQuoteConsumed(result.displayQuoteNo); }
      }
    } catch(e){
      alert('Could not save quote: '+e.message);
      w.close();
      return;
    } finally{
      setSaving(false);
    }
    if(!printed) setPrinted(true);
    let content=document.getElementById("print-doc").innerHTML.replace(previewQuoteNo,effectiveQuoteNo);
    content=content.replace(/src="\/CS_Software_DinoLogo\.png"/g,`src="${window.location.origin}/CS_Software_DinoLogo.png"`);
    const name=(header.customerName||"Customer").replace(/[^a-zA-Z0-9]/g,"_");
    const loc=(header.address||"Location").replace(/[^a-zA-Z0-9]/g,"_").slice(0,25);
    const fn=`${effectiveQuoteNo}_${name}_${loc}`;
    w.document.open();
    w.document.write(`<!DOCTYPE html><html><head><title>${fn}</title>
      <style>body{margin:0;padding:20px;font-family:'Segoe UI',sans-serif;background:white;color:#111;}*{box-sizing:border-box;}table{width:100%;border-collapse:collapse;}@media print{@page{margin:0;size:A4;}body{padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}}</style>
      </head><body>${content}</body></html>`);
    w.document.close();
    setTimeout(()=>{w.focus();w.print();},600);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.88)",zIndex:100,display:"flex",justifyContent:"center",alignItems:"center"}}>
      <div style={{maxHeight:"90vh",width:"100%",maxWidth:940,display:"flex",flexDirection:"column",overflowY:"auto",borderRadius:10,boxShadow:"0 20px 70px rgba(0,0,0,0.4)"}}>
      <div id="print-doc" style={{background:"white",color:"#111",width:"100%",fontFamily:"'Segoe UI',sans-serif"}}>
        {/* ── Letterhead ── */}
        <div style={{padding:"28px 36px 20px",borderBottom:"4px solid #9b2d7f"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:20}}>
            <div>
              <img src={drapeiqLogo} alt="DrapeIQ" style={{height:100,objectFit:"contain",display:"block"}}/>
              <div style={{fontSize:10,letterSpacing:"3px",color:"#999",marginTop:7,fontWeight:600,textTransform:"uppercase"}}>Soft Furnishings Quotation Platform</div>
              <div style={{fontSize:12,color:"#555",marginTop:9,lineHeight:1.7}}>
                {tenant?.address}<br/>
                <span style={{fontWeight:600}}>📞</span> {tenant?.contact_phone} &nbsp;|&nbsp; <span style={{fontWeight:600}}>✉</span> {tenant?.contact_email}
              </div>
            </div>
            <div style={{textAlign:"right",flexShrink:0}}>
              <div style={{fontSize:26,fontWeight:700,color:"#ccc",letterSpacing:"4px",marginBottom:10,fontFamily:"'Segoe UI',sans-serif"}}>QUOTATION</div>
              <table style={{fontSize:12,color:"#555",marginLeft:"auto"}}><tbody>
                <tr><td style={{paddingRight:12,fontWeight:700,paddingBottom:4}}>Quote No.</td><td style={{fontFamily:"'Courier New',monospace",fontWeight:900,color:"#111"}}>{previewQuoteNo}</td></tr>
                <tr><td style={{paddingRight:12,fontWeight:700}}>Date</td><td>{header.date}</td></tr>
              </tbody></table>
            </div>
          </div>
          <div style={{marginTop:18,paddingTop:18,borderTop:"1px solid #eee"}}>
            <div style={{fontSize:10,textTransform:"uppercase",letterSpacing:"2px",color:"#aaa",fontWeight:700,marginBottom:5}}>Bill To</div>
            <div style={{fontSize:19,fontWeight:700,color:"#111"}}>{header.customerName||"—"}</div>
            <div style={{fontSize:13,color:"#666",marginTop:3}}>{header.address||"—"}</div>
          </div>
        </div>
        {/* ── Body ── */}
        <div style={{padding:"20px 36px"}}>
          {rooms.map((room,ri)=>{
            const rt=roomTotal(room).total;
            return (
              <div key={room.id} style={ri>0?{marginTop:24,paddingTop:24,borderTop:"1px solid #eee"}:{}}>
                <div style={{fontWeight:700,fontSize:13,textTransform:"uppercase",letterSpacing:"1px",color:"#444",marginBottom:9,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                  <span>{room.floor} · {room.roomType}</span>
                  <span style={{fontSize:11,background:"#fdf0fb",color:"#6b1a5a",padding:"3px 10px",borderRadius:20,fontFamily:"'Courier New',monospace"}}>₹{fmt(rt)}</span>
                </div>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:4}}>
                  <thead><tr>{["Description","Qty","Rate","Amount"].map(h=><th key={h} style={{...ps.pth,textAlign:["Qty","Rate","Amount"].includes(h)?"right":"left"}}>{h}</th>)}</tr></thead>
                  <tbody>
                    {room.windows.map(win=>[
                      ...win.panels.map(p=>renderPanel(win,p)),
                      ...[["trim","Trim"],["border","Border"],["liningB","Lining (Blackout)"],["liningN","Lining (Normal)"]].map(([k,label])=>{
                        const qty=parseFloat(win.extras[`${k}Qty`])||0,mrp=parseFloat(win.extras[`${k}Mrp`])||0,amt=qty*mrp;
                        if(!amt) return null;
                        return <tr key={k}><td style={ps.ptd}>{win.name} · {label}</td><td style={ps.ptdR}>{qty}m</td><td style={ps.ptdR}>₹{fmt(mrp)}/m</td><td style={ps.ptdA}>₹{fmt(amt)}</td></tr>;
                      })
                    ])}
                    {room.hardware.map(hw=>{
                      const amt=calcHW(hw); if(!amt) return null;
                      let desc=hw.category,q="—",r="—";
                      if(hw.category==="Roman DLX"){const rf=ceilVal((parseFloat(hw.width)||0)/12);desc="Roman DLX Channel";q=`${rf} rf`;r=`₹${fmt(hw.rate)}/rf`;}
                      else if(CURTAIN_ROD_OPTIONS.includes(hw.category)){q=hw.qty;r=`₹${fmt(hw.price)}`;}
                      else{q=hw.qty;r=`₹${fmt(hw.mrp)} (−${hw.discount}%)`;}
                      return <tr key={hw.id}><td style={ps.ptd}>{desc}</td><td style={ps.ptdR}>{q}</td><td style={ps.ptdR}>{r}</td><td style={ps.ptdA}>₹{fmt(amt)}</td></tr>;
                    })}
                    <tr style={{background:"#fdf0fb"}}><td colSpan={3} style={{...ps.ptd,textAlign:"right",fontWeight:700,color:"#6b1a5a",fontSize:13}}>Room Total</td><td style={{...ps.ptdA,fontWeight:900,fontSize:14}}>₹{fmt(rt)}</td></tr>
                  </tbody>
                </table>
                {room.notes&&<p style={{fontSize:11,color:"#999",fontStyle:"italic",marginTop:3}}>Note: {room.notes}</p>}
              </div>
            );
          })}
          {fitTotal>0&&(
            <div style={{marginTop:14,paddingTop:14,borderTop:"1px solid #eee"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}><tbody>
                <tr><td style={ps.ptd}>Fitting Charges ({wc} windows × ₹{fmt(fitting.rate)}/window)</td><td style={ps.ptdR}>{wc} windows</td><td style={ps.ptdR}>₹{fmt(fitting.rate)}/window</td><td style={ps.ptdA}>₹{fmt(fitTotal)}</td></tr>
              </tbody></table>
            </div>
          )}
        </div>
        {/* ── Footer ── */}
        <div style={{padding:"0 36px 28px"}}>
          <div style={{borderTop:"2px solid #111",paddingTop:12,marginBottom:14}}>
            <table style={{width:"100%",fontSize:13}}><tbody>
              <tr>
                <td style={{textAlign:"right",fontWeight:700,padding:"5px 0",fontSize:14}}>Grand Total</td>
                <td style={{textAlign:"right",fontFamily:"'Courier New',monospace",fontWeight:900,fontSize:22,paddingLeft:24,color:"#111"}}>₹{fmt(Math.round(grand))}</td>
              </tr>
              <tr>
                <td style={{textAlign:"right",fontWeight:700,padding:"3px 0",fontSize:13,color:"#059669"}}>To Be Paid</td>
                <td style={{textAlign:"right",fontFamily:"'Courier New',monospace",fontWeight:900,fontSize:18,paddingLeft:24,color:"#059669"}}>₹{fmt(toBePaid)}</td>
              </tr>
            </tbody></table>
          </div>
          <div style={{background:"#fdf5fc",border:"1px solid #f0e0f0",borderRadius:8,padding:"10px 14px",fontSize:12,color:"#555",marginBottom:18}}>
            <strong>Amount in Words:</strong> {toWords(Math.round(grand))}
          </div>
          <div style={{fontSize:11,fontWeight:700,textTransform:"uppercase",letterSpacing:"2px",color:"#333",marginBottom:7}}>Terms &amp; Conditions</div>
          {TERMS.map((t,i)=><div key={i} style={{fontSize:11,color:"#666",marginBottom:4}}>{i+1}. {t}</div>)}
          <div style={{display:"flex",justifyContent:"flex-end",marginTop:22}}>
            <div style={{textAlign:"center",borderTop:"1px solid #bbb",paddingTop:8,paddingLeft:28,paddingRight:28}}>
              <div style={{fontSize:11,color:"#888"}}>For {tenant?.name || '—'}</div>
              <div style={{fontSize:11,color:"#888",marginTop:4}}>Authorized Signatory</div>
            </div>
          </div>
        </div>
      </div>
      {/* Sticky button bar */}
      <div style={{position:"sticky",bottom:0,background:"white",padding:"14px 22px",display:"flex",flexDirection:"column",alignItems:"flex-end",gap:9,borderTop:"1px solid #eee"}}>
        {printed&&<div style={{background:"rgba(5,150,105,0.15)",border:"1px solid rgba(5,150,105,0.3)",borderRadius:8,padding:"6px 13px",...FONT.small,color:"#059669",fontFamily:"monospace"}}>✓ {previewQuoteNo} confirmed</div>}
        <div style={{display:"flex",gap:10}}>
          <button onClick={handlePrint} disabled={saving} style={{background:saving?"#c47aaa":"#9b2d7f",color:"#fff",fontWeight:700,border:"none",borderRadius:9,padding:"12px 22px",cursor:saving?"not-allowed":"pointer",fontSize:14,boxShadow:"0 4px 18px rgba(155,45,127,0.3)"}}>
            {saving?"Saving...":printed?"🖨 Print Again":"🖨 Print / Save PDF"}
          </button>
          <button onClick={onClose} style={{background:"#555",color:"white",fontWeight:700,border:"none",borderRadius:9,padding:"12px 22px",cursor:"pointer",fontSize:14}}>✕ Close</button>
        </div>
      </div>
      </div>
    </div>
  );
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const {session,login,logout}=useAuth();
  // FIX #3: Default light theme
  const [darkMode,setDarkMode]=useState(false);
  const C=THEMES[darkMode?"dark":"light"];
  const s=makeStyles(C);

  const [header,setHeader]=useState({customerName:"",address:"",quoteNo:"(assigned on print)",date:new Date().toLocaleDateString("en-IN")});
  const [rooms,setRooms]=useState([newRoom("GF","LIVING")]);
  const [fitting,setFitting]=useState({rate:"250"});
  const [hasChanges, setHasChanges] = useState(false);
  const [customToBePaid, setCustomToBePaid] = useState(null);
  const [preview,setPreview]=useState(false);
  const [extraMfrs,setExtraMfrs]=useState([]);
  const [extraAcc,setExtraAcc]=useState([]);
  const [extraRodTypes,setExtraRodTypes]=useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [tenantDetails, setTenantDetails] = useState(null);
  useEffect(() => {
    if (!session) return;
    getTenantDetails().then(setTenantDetails).catch(console.error);
  }, [session]);

  const [customCurtainTypes,setCustomCurtainTypes]=useState({});
  const handleAddCustomType=(seg,name)=>setCustomCurtainTypes(p=>({...p,[seg]:[...(p[seg]||[]).filter(t=>t!==name),name]}));

  // Dynamic room type & floor lists (extensible via custom entries)
  const [customRoomTypes,setCustomRoomTypes]=useState([]);
  const [customFloors,setCustomFloors]=useState([]);
  const allRoomTypes=useMemo(()=>[...DEFAULT_ROOM_TYPES,...customRoomTypes.filter(r=>!DEFAULT_ROOM_TYPES.includes(r))],[customRoomTypes]);
  const allFloors=useMemo(()=>[...DEFAULT_FLOORS,...customFloors.filter(f=>!DEFAULT_FLOORS.includes(f))],[customFloors]);

  const grand=grandTotal(rooms)+totalWins(rooms)*(parseFloat(fitting.rate)||0);
  const wc=totalWins(rooms);
  const autoToBePaid=roundToNearest500(grand*0.8);
  const effectiveToBePaid=customToBePaid !== null ? customToBePaid : autoToBePaid;

  const updRoom=useCallback((id,r)=>{ setRooms(p=>p.map(x=>x.id===id?r:x)); setHasChanges(true); const newRT=r.roomType; if(!DEFAULT_ROOM_TYPES.includes(newRT)&&newRT) setCustomRoomTypes(p=>[...p.filter(x=>x!==newRT),newRT]); const newF=r.floor; if(!DEFAULT_FLOORS.includes(newF)&&newF) setCustomFloors(p=>[...p.filter(x=>x!==newF),newF]); },[setHasChanges]);
  const delRoom=useCallback((id)=>{ setRooms(p=>p.filter(x=>x.id!==id)); setHasChanges(true); },[setHasChanges]);
  const handleQuoteConsumed=(qNo)=>setHeader(h=>({...h,quoteNo:qNo}));

  // Group rooms by floor
  const floorOrder=useMemo(()=>{
    const seen=new Set(); const order=[];
    rooms.forEach(r=>{ if(!seen.has(r.floor)){seen.add(r.floor);order.push(r.floor);} });
    return order;
  },[rooms]);

  const addRoomToFloor=(floor)=>{
    const floorRooms=rooms.filter(r=>r.floor===floor);
    const usedTypes=new Set(floorRooms.map(r=>r.roomType));
    const nextType=allRoomTypes.find(t=>!usedTypes.has(t))||"ROOM";
    setRooms(p=>[...p,newRoom(floor,nextType)]);
  };

  const addNewFloor=()=>{
    const usedFloors=new Set(rooms.map(r=>r.floor));
    const nextFloor=allFloors.find(f=>!usedFloors.has(f));
    if(nextFloor){ setRooms(p=>[...p,newRoom(nextFloor,"LIVING")]); }
    else { const cf=`F${rooms.length+1}`; setCustomFloors(p=>[...p,cf]); setRooms(p=>[...p,newRoom(cf,"LIVING")]); }
  };

  const [screen, setScreen] = useState('landing');
  const [loadedQuote, setLoadedQuote] = useState(null);
  const [savedQuoteId, setSavedQuoteId] = useState(null);

  const resetAllState = () => {
    setHeader({customerName:"",address:"",quoteNo:"(assigned on print)",date:new Date().toLocaleDateString("en-IN")});
    setRooms([newRoom("GF","LIVING")]);
    setFitting({rate:"250"});
    setExtraMfrs([]); setExtraAcc([]); setExtraRodTypes([]);
    setCustomCurtainTypes({}); setCustomRoomTypes([]); setCustomFloors([]);
    setPreview(false);
    setHasChanges(false);
    setCustomToBePaid(null);
  };

  const loadQuoteIntoState = (quote) => {
    const d = quote.quote_data;
    setHeader(d.header);
    setRooms(d.rooms);
    setFitting(d.fitting);
    setExtraMfrs(d.extraMfrs||[]);
    setExtraAcc(d.extraAcc||[]);
    setExtraRodTypes(d.extraRodTypes||[]);
    setCustomCurtainTypes(d.customCurtainTypes||{});
    setCustomRoomTypes(d.customRoomTypes||[]);
    setCustomFloors(d.customFloors||[]);
    setPreview(false);
    setLoadedQuote(quote);
    setSavedQuoteId(quote.id);
    setCustomToBePaid(quote.to_be_paid ?? null);
    setHasChanges(false);
  };

  useEffect(()=>{ setHasChanges(false); },[loadedQuote]);

  if(session === undefined) return null;
  if(!session) return <LoginScreen onLogin={login}/>;

  if(screen === 'landing') return (
    <LandingScreen
      onNewQuote={() => { resetAllState(); setLoadedQuote(null); setSavedQuoteId(null); setHasChanges(false); setScreen('app'); }}
      onLoadQuote={(quote) => { loadQuoteIntoState(quote); setScreen('app'); }}
      onAnalytics={() => setScreen('analytics')}
      onLogout={logout}
      userEmail={session?.user?.email}
    />
  );

  if(screen === 'analytics') return (
    <AnalyticsDashboard onBack={() => setScreen('landing')} />
  );

  return (
    <div style={s.page}>
      {/* Top bar */}
      <div style={{...s.topbar,...(isMobile&&{flexDirection:'column',alignItems:'stretch',padding:'10px 16px',gap:6})}}>
        <div style={{display:"flex",alignItems:"center",gap:16}}>
          <button onClick={()=>setScreen('landing')} style={{background:"none",border:"none",cursor:"pointer",color:"#a07090",fontSize:12,letterSpacing:"0.5px",padding:"4px 2px",fontFamily:"inherit",opacity:0.75}} title="Back to Home">← Home</button>
          <div style={isMobile?{display:'flex',flexDirection:'column',alignItems:'center'}:undefined}>
            <img src={drapeiqLogo} alt="DrapeIQ" onClick={()=>setScreen('landing')} style={{height:isMobile?36:56,objectFit:"contain",cursor:"pointer"}}/>
            <div style={s.subtitle}>DrapeIQ · Soft Furnishings</div>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:14,...(isMobile&&{justifyContent:'space-between'})}}>
          <div style={{textAlign:"right"}}>
            <div style={s.grandLabel}>Grand Total</div>
            <div style={s.grandValue}>₹{fmt(grand)}</div>
          </div>
          <button onClick={()=>setDarkMode(d=>!d)} style={s.themeBtn} title={darkMode?"Light Mode":"Dark Mode"}>{darkMode?"☀️":"🌙"}</button>
          <button onClick={()=>setPreview(true)} style={s.previewBtn}>Preview & Print →</button>
        </div>
      </div>

      <div style={s.container}>
        {/* Project Details */}
        <div style={s.card}>
          {/* FIX #8: bold section heading */}
          <span style={{...FONT.sectionLabel,...FONT.heading3,color:C.textMuted,display:"block",marginBottom:14,letterSpacing:"2px"}}>Project Details</span>
          <div style={{...s.grid4,marginBottom:14,...(isMobile && {gridTemplateColumns:'1fr 1fr'})}}>
            <Field label="Customer / Project Name" value={header.customerName} onChange={v=>{setHeader(h=>({...h,customerName:v}));setHasChanges(true);}} placeholder="e.g. Rahul Sharma" C={C} s={s}/>
            <Field label="Address / Location" value={header.address} onChange={v=>{setHeader(h=>({...h,address:v}));setHasChanges(true);}} placeholder="Property address" C={C} s={s}/>
            <Field label="Quotation No" value={header.quoteNo} onChange={()=>{}} readOnly C={C} s={s}/>
            <Field label="Date" value={header.date} onChange={v=>{setHeader(h=>({...h,date:v}));setHasChanges(true);}} C={C} s={s}/>
          </div>
          <div style={{marginTop:9,...FONT.tiny,color:C.textDim,fontStyle:"italic"}}>
            💡 Quote number auto-assigned on first print — no number is wasted.
          </div>
        </div>

        {/* Floor + Room layout */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <span style={{...FONT.sectionLabel,color:C.textMuted}}>
            Rooms <span style={{color:C.pink}}>({rooms.length})</span>
            <span style={{marginLeft:14,color:C.textDim,fontWeight:400}}>· {wc} window{wc!==1?"s":""}</span>
          </span>
          <button onClick={addNewFloor} style={{...s.addBtn,padding:"8px 18px",...FONT.small}}>+ Add Floor</button>
        </div>

        {floorOrder.map(floor=>{
          const floorRooms=rooms.filter(r=>r.floor===floor);
          return (
            <FloorBlock key={floor} floor={floor} rooms={floorRooms}
              onChangeRoom={(id,r)=>updRoom(id,r)}
              onDeleteRoom={(id)=>delRoom(id)}
              onAddRoom={()=>addRoomToFloor(floor)}
              extraMfrs={extraMfrs} onAddMfr={m=>setExtraMfrs(p=>[...p.filter(x=>x!==m),m])}
              extraAccessories={extraAcc} onAddAccessory={a=>setExtraAcc(p=>[...p.filter(x=>x!==a),a])}
              extraRodTypes={extraRodTypes} onAddRodType={r=>setExtraRodTypes(p=>[...p.filter(x=>x!==r),r])}
              customCurtainTypes={customCurtainTypes} onAddCustomType={handleAddCustomType}
              C={C} s={s} allRoomTypes={allRoomTypes} allFloors={allFloors}/>
          );
        })}

        <FittingSection fitting={fitting} onUpdate={(v)=>{setFitting(v);setHasChanges(true);}} windowCount={wc} C={C} s={s}/>

        {/* Summary */}
        <div style={s.card}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:18}}>
            <div style={{flex:1,minWidth:280}}>
              <span style={{...FONT.sectionLabel,color:C.textMuted,display:"block",marginBottom:12}}>Summary</span>
              {rooms.map(r=>(
                <div key={r.id} style={{display:"flex",justifyContent:"space-between",gap:28,...FONT.body,color:C.textMuted,marginBottom:4,borderBottom:`1px solid rgba(155,45,127,0.08)`,paddingBottom:4}}>
                  <span>{r.floor} · {r.roomType}</span>
                  <span style={{...FONT.mono,fontSize:13,color:"rgba(155,45,127,0.75)"}}>₹{fmt(roomTotal(r).total)}</span>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",gap:28,...FONT.body,color:C.textMuted,marginTop:8,paddingTop:8,borderTop:`1px dashed rgba(155,45,127,0.15)`}}>
                <span>Fitting ({wc} × ₹{fitting.rate})</span>
                <span style={{...FONT.mono,fontSize:13,color:"rgba(155,45,127,0.75)"}}>₹{fmt(wc*(parseFloat(fitting.rate)||0))}</span>
              </div>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={s.grandLabel}>Grand Total</div>
              <div style={{...FONT.mono,fontSize:34,color:C.pink}}>₹{fmt(grand)}</div>
              <div style={{...FONT.tinyBold,color:C.textDim,marginTop:6,letterSpacing:"1px",textTransform:"uppercase"}}>To Be Paid</div>
              <div style={{display:"flex",alignItems:"baseline",gap:2,marginTop:2,justifyContent:"flex-end"}}>
                <span style={{...FONT.mono,fontSize:28,color:C.green}}>₹</span>
                <input
                  type="number"
                  value={effectiveToBePaid}
                  onChange={e=>{
                    const v=parseInt(e.target.value,10);
                    setCustomToBePaid(isNaN(v)?null:v);
                    setHasChanges(true);
                  }}
                  style={{...FONT.mono,fontSize:28,color:C.green,background:"transparent",border:"none",borderBottom:`2px solid ${customToBePaid!==null?C.pink:C.border}`,outline:"none",width:160,textAlign:"right",padding:"2px 0",fontFamily:"'Courier New',monospace"}}
                />
              </div>
              {customToBePaid===null
                ? <div style={{...FONT.tiny,color:C.textDim,marginTop:4,fontStyle:"italic"}}>Auto-calculated at 80%</div>
                : <button onClick={()=>{setCustomToBePaid(null);setHasChanges(true);}} style={{background:"none",border:"none",color:C.pink,fontSize:11,cursor:"pointer",marginTop:4,fontFamily:"inherit",opacity:0.85,padding:0}}>↺ Reset to 80%</button>
              }
            </div>
          </div>
        </div>

        {/* Terms */}
        <div style={{marginTop:6,background:C.termsBg,border:`1px solid ${C.border}`,borderRadius:10,padding:16,...FONT.small,color:C.textDim}}>
          <div style={{...FONT.smallBold,color:C.textMuted,marginBottom:8,textTransform:"uppercase",letterSpacing:"1px"}}>Standard Terms</div>
          {TERMS.map((t,i)=><div key={i} style={{marginBottom:4}}>{i+1}. {t}</div>)}
        </div>
      </div>

      {preview&&<PrintPreview header={header} rooms={rooms} fitting={fitting} onClose={()=>setPreview(false)} onQuoteConsumed={handleQuoteConsumed} savedQuoteId={savedQuoteId} setSavedQuoteId={setSavedQuoteId} loadedQuote={loadedQuote} hasChanges={hasChanges} existingQuoteNo={loadedQuote ? loadedQuote.display_quote_no : null} toBePaidOverride={effectiveToBePaid} tenant={tenantDetails}
        onRevisionSaved={(result) => {
          setHasChanges(false);
          setLoadedQuote(prev => ({...prev, revision: prev.revision+1, id: result.id, display_quote_no: result.displayQuoteNo}));
          setSavedQuoteId(result.id);
        }}/>}
    </div>
  );
}